/**
 * The document editor's own look, light or dark, set apart from the app's theme (Word's dark
 * mode): like the app (the default), always light or always dark. Kept by the browser, outside
 * the editor so it stays while documents open and close; printing is always on white paper.
 */
import { createSignal, onCleanup, type Accessor } from "solid-js";
import { load, save } from "../../../shared/storage";
import type { ThemeMode } from "../../../state/theme";

/** The choices: like the app, light or dark. */
export const DOC_LOOKS = [
  { id: "app", label: "Like the app" },
  { id: "light", label: "Light" },
  { id: "dark", label: "Dark" },
] as const;

/** One of {@link DOC_LOOKS}. */
export type DocLook = (typeof DOC_LOOKS)[number]["id"];

/** Where the choice is kept in the browser. */
const KEY = "developer-app-doc-look-v1";

const saved = load<DocLook>(KEY, "app");
const [look, setLookSignal] = createSignal<DocLook>(DOC_LOOKS.some((l) => l.id === saved) ? saved : "app");

/** The editor's look chosen. */
export const docLook: Accessor<DocLook> = look;

/** Chooses the editor's look and remembers it. */
export function setDocLook(next: DocLook): void {
  setLookSignal(next);
  save(KEY, next);
}

/** Whether the system asks for dark, followed while the calling component lives. */
function systemDark(): Accessor<boolean> {
  const query = typeof matchMedia === "function" ? matchMedia("(prefers-color-scheme: dark)") : null;
  const [dark, setDark] = createSignal(!!query?.matches);
  const changed = (e: MediaQueryListEvent) => setDark(e.matches);
  query?.addEventListener("change", changed);
  onCleanup(() => query?.removeEventListener("change", changed));
  return dark;
}

/** Light or dark, as the editor is drawn now (call inside a component): the choice, else the app's theme. */
export function shownLook(app: Accessor<ThemeMode>): Accessor<"light" | "dark"> {
  const dark = systemDark();
  return () => {
    const chosen = look();
    if (chosen !== "app") return chosen;
    const mode = app();
    return mode === "system" ? (dark() ? "dark" : "light") : mode;
  };
}
